import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import SignatureCanvas from 'react-signature-canvas';
import {
  PackageCheck, Plus, Trash2, FileText, ArrowLeft, Loader2, CheckCircle2, ExternalLink, Search, X
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY_ROW = {
  item_no: '', qty: '', bo: '', part_no: '', description: '',
  wt_pc: '', total_wt: '', truck_no: '', driver_initial: ''
};

const COLS = [
  { key: 'item_no', label: 'Item #', w: 'w-14' },
  { key: 'qty', label: 'QTY', w: 'w-14' },
  { key: 'bo', label: 'B/O', w: 'w-12' },
  { key: 'part_no', label: 'Part #', w: 'w-24' },
  { key: 'description', label: 'Description', w: 'flex-1 min-w-[180px]' },
  { key: 'wt_pc', label: 'Wt/pc', w: 'w-16' },
  { key: 'total_wt', label: 'Total Wt', w: 'w-16' },
  { key: 'truck_no', label: 'Truck #', w: 'w-16' },
  { key: 'driver_initial', label: 'Driver', w: 'w-16' },
];

const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const PackingSlips = () => {
  const [view, setView] = useState('list'); // 'list' | 'builder'
  const [slips, setSlips] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadData = async () => {
    setLoading(true);
    try {
      const [sRes, pRes] = await Promise.all([
        axios.get(`${API}/packing-slips`, authHeader()),
        axios.get(`${API}/projects`, authHeader()),
      ]);
      setSlips(sRes.data || []);
      setProjects(pRes.data || []);
    } catch (e) {
      toast.error('Failed to load packing slips');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const openPdf = (slipId) => {
    const token = localStorage.getItem('token');
    window.open(`${API}/packing-slips/${slipId}/pdf?token=${token}`, '_blank');
  };

  if (view === 'builder') {
    return (
      <PackingSlipBuilder
        projects={projects}
        onCancel={() => setView('list')}
        onCreated={(slip) => { setView('list'); loadData(); if (slip?.id) openPdf(slip.id); }}
      />
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="packing-slips-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="bg-[#1e3a5f] p-2.5 rounded-lg">
            <PackageCheck className="text-white" size={22} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Packing Slips</h1>
            <p className="text-sm text-blue-800">Create and file packing slips to a work order</p>
          </div>
        </div>
        <button
          onClick={() => setView('builder')}
          data-testid="new-packing-slip-btn"
          className="inline-flex items-center gap-2 bg-[#1e3a5f] hover:bg-[#16304f] text-white px-4 py-2.5 rounded-lg font-medium transition-colors"
        >
          <Plus size={18} /> New Packing Slip
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-500">
          <Loader2 className="animate-spin mr-2" /> Loading…
        </div>
      ) : slips.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-xl border border-gray-200" data-testid="packing-slips-empty">
          <PackageCheck className="mx-auto text-slate-700 mb-3" size={48} />
          <p className="text-gray-500">No packing slips yet. Create your first one.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm" data-testid="packing-slips-table">
            <thead className="bg-gray-50 text-gray-600 text-left">
              <tr>
                <th className="px-4 py-3 font-semibold">Work Order</th>
                <th className="px-4 py-3 font-semibold">Date</th>
                <th className="px-4 py-3 font-semibold">Packed By</th>
                <th className="px-4 py-3 font-semibold">Drive</th>
                <th className="px-4 py-3 font-semibold text-right">PDF</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {slips.map((s) => (
                <tr key={s.id} className="hover:bg-gray-50" data-testid={`packing-slip-row-${s.id}`}>
                  <td className="px-4 py-3 font-medium text-gray-900">
                    {s.project_number || '—'}
                    {s.project_title ? <span className="text-slate-500 font-normal"> · {s.project_title}</span> : null}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{s.slip_date || '—'}</td>
                  <td className="px-4 py-3 text-gray-600">{s.packer_name || s.created_by_name || '—'}</td>
                  <td className="px-4 py-3">
                    {s.drive_uploaded ? (
                      <span className="inline-flex items-center gap-1 text-green-600 text-xs font-medium">
                        <CheckCircle2 size={14} /> Filed
                      </span>
                    ) : (
                      <span className="text-amber-600 text-xs font-medium">Local only</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => openPdf(s.id)}
                      data-testid={`view-pdf-${s.id}`}
                      className="inline-flex items-center gap-1 text-[#1e3a5f] hover:underline font-medium"
                    >
                      <FileText size={15} /> View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, ...props }) => (
  <div>
    <label className="block text-xs font-semibold text-[#1e3a5f] mb-1 uppercase tracking-wide">{label}</label>
    <input
      {...props}
      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#1e3a5f] focus:border-transparent outline-none"
    />
  </div>
);

const PackingSlipBuilder = ({ projects, onCancel, onCreated }) => {
  const sigRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    project_id: '',
    po_number: '',
    job_number: '',
    slip_date: new Date().toISOString().slice(0, 10),
    ship_to: '',
    bill_to: '',
    same_as_shipping: false,
    contact: '',
    phone: '',
    mobile: '',
    return_authorization: '',
    original_po: '',
    doc_type: 'packing_slip',
    doc_type_other: '',
    total_weight: '',
    receiver_name_printed: '',
  });
  const [rows, setRows] = useState([{ ...EMPTY_ROW }]);
  const [woQuery, setWoQuery] = useState('');
  const [showWo, setShowWo] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSelectProject = (id) => {
    const p = projects.find((x) => x.id === id);
    if (!p) { set('project_id', ''); return; }
    setForm((f) => ({
      ...f,
      project_id: id,
      job_number: p.project_number || '',
      po_number: f.po_number || p.project_number || '',
      bill_to: p.client_name || f.bill_to,
      ship_to: [p.site_name, p.site_address].filter(Boolean).join('\n') || f.ship_to,
      contact: p.site_contact_name || f.contact,
      phone: p.site_contact_phone || f.phone,
    }));
  };

  const woQ = woQuery.trim().toLowerCase();
  const woMatches = (woQ
    ? projects.filter((p) =>
        [p.project_number, p.title, p.client_name]
          .filter(Boolean)
          .some((s) => String(s).toLowerCase().includes(woQ)))
    : projects
  ).slice(0, 25);

  const selectWorkOrder = (p) => {
    onSelectProject(p.id);
    setWoQuery(`${p.project_number}${p.title ? ` · ${p.title}` : ''}`);
    setShowWo(false);
  };
  const clearWorkOrder = () => { set('project_id', ''); setWoQuery(''); setShowWo(true); };

  const updateRow = (i, key, val) => {
    setRows((r) => r.map((row, idx) => (idx === i ? { ...row, [key]: val } : row)));
  };
  const addRow = () => setRows((r) => [...r, { ...EMPTY_ROW }]);
  const removeRow = (i) => setRows((r) => (r.length > 1 ? r.filter((_, idx) => idx !== i) : r));

  const submit = async () => {
    if (!form.project_id) { toast.error('Please select a work order'); return; }
    if (!sigRef.current || sigRef.current.isEmpty()) { toast.error('Packer signature is required'); return; }

    const signature = sigRef.current.getCanvas().toDataURL('image/png');
    const line_items = rows
      .map((r, idx) => ({ ...r, item_no: r.item_no || (Object.values(r).some((v) => v) ? String(idx + 1) : '') }))
      .filter((r) => Object.values(r).some((v) => String(v).trim() !== ''));

    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/packing-slips`,
        { ...form, line_items, packer_signature: signature },
        authHeader()
      );
      toast.success(res.data?.drive_uploaded
        ? 'Packing slip created and filed to Drive'
        : 'Packing slip created (filed locally — Drive folder not found for this WO)');
      onCreated(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create packing slip');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="packing-slip-builder">
      <button onClick={onCancel} className="inline-flex items-center gap-1 text-gray-500 hover:text-gray-800 mb-4 text-sm">
        <ArrowLeft size={16} /> Back to list
      </button>

      <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-6">
        <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <PackageCheck size={20} className="text-[#1e3a5f]" /> New Packing Slip
        </h2>

        {/* Work order — type to search */}
        <div className="relative">
          <label className="block text-xs font-semibold text-[#1e3a5f] mb-1 uppercase tracking-wide">Work Order *</label>
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
            <input
              type="text"
              value={woQuery}
              onChange={(e) => { setWoQuery(e.target.value); setShowWo(true); if (form.project_id) set('project_id', ''); }}
              onFocus={() => setShowWo(true)}
              onBlur={() => setTimeout(() => setShowWo(false), 150)}
              placeholder="Start typing a work order #, client or title…"
              data-testid="work-order-search"
              autoComplete="off"
              className="w-full border border-gray-300 rounded-md pl-9 pr-9 py-2 text-sm focus:ring-2 focus:ring-[#1e3a5f] focus:border-transparent outline-none bg-white"
            />
            {(woQuery || form.project_id) && (
              <button
                type="button"
                onClick={clearWorkOrder}
                data-testid="work-order-clear"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-gray-700"
              >
                <X size={16} />
              </button>
            )}
          </div>
          {showWo && (
            <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-64 overflow-y-auto" data-testid="work-order-suggestions">
              {woMatches.length === 0 ? (
                <div className="px-3 py-3 text-sm text-slate-500">No matching work orders</div>
              ) : (
                woMatches.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => selectWorkOrder(p)}
                    data-testid={`work-order-option-${p.id}`}
                    className={`w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-50 last:border-0 ${form.project_id === p.id ? 'bg-[#1e3a5f]/5' : ''}`}
                  >
                    <span className="font-medium text-gray-900 text-sm">{p.project_number}</span>
                    {(p.title || p.client_name) && <span className="text-gray-500 text-sm"> · {p.title || p.client_name}</span>}
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        {/* Document type */}
        <div className="flex flex-wrap items-center gap-5">
          {[
            { v: 'packing_slip', l: '1. Packing Slip' },
            { v: 'return_of_material', l: '2. Return of Material' },
            { v: 'other', l: '3. Other' },
          ].map((opt) => (
            <label key={opt.v} className="inline-flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
              <input
                type="radio"
                name="doc_type"
                checked={form.doc_type === opt.v}
                onChange={() => set('doc_type', opt.v)}
                data-testid={`doc-type-${opt.v}`}
                className="accent-[#1e3a5f]"
              />
              {opt.l}
            </label>
          ))}
          {form.doc_type === 'other' && (
            <input
              value={form.doc_type_other}
              onChange={(e) => set('doc_type_other', e.target.value)}
              placeholder="Specify…"
              data-testid="doc-type-other-input"
              className="border border-gray-300 rounded-md px-2 py-1 text-sm"
            />
          )}
        </div>

        {/* Header fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-[#1e3a5f] mb-1 uppercase tracking-wide">Ship To</label>
            <textarea
              value={form.ship_to}
              onChange={(e) => set('ship_to', e.target.value)}
              rows={2}
              data-testid="ship-to-input"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#1e3a5f] outline-none"
            />
          </div>
          <div className="space-y-4">
            <Field label="Date" type="date" value={form.slip_date} onChange={(e) => set('slip_date', e.target.value)} data-testid="slip-date-input" />
            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={form.same_as_shipping}
                onChange={(e) => set('same_as_shipping', e.target.checked)}
                data-testid="same-as-shipping-checkbox"
                className="accent-[#1e3a5f]"
              />
              Bill To same as Shipping Address
            </label>
          </div>
          <Field label="Bill To" value={form.bill_to} onChange={(e) => set('bill_to', e.target.value)} data-testid="bill-to-input" />
          <Field label="Job Number" value={form.job_number} onChange={(e) => set('job_number', e.target.value)} data-testid="job-number-input" />
          <Field label="Contact" value={form.contact} onChange={(e) => set('contact', e.target.value)} data-testid="contact-input" />
          <Field label="Phone" value={form.phone} onChange={(e) => set('phone', e.target.value)} data-testid="phone-input" />
          <Field label="Mobile" value={form.mobile} onChange={(e) => set('mobile', e.target.value)} data-testid="mobile-input" />
          <Field label="PO Number" value={form.po_number} onChange={(e) => set('po_number', e.target.value)} data-testid="po-number-input" />
          <Field label="Return Authorization #" value={form.return_authorization} onChange={(e) => set('return_authorization', e.target.value)} data-testid="return-auth-input" />
          <Field label="Original PO #" value={form.original_po} onChange={(e) => set('original_po', e.target.value)} data-testid="original-po-input" />
        </div>

        {/* Line items */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-bold text-[#1e3a5f] uppercase tracking-wide">Line Items</h3>
            <button onClick={addRow} data-testid="add-row-btn" className="inline-flex items-center gap-1 text-sm text-[#1e3a5f] font-medium hover:underline">
              <Plus size={15} /> Add Row
            </button>
          </div>
          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="w-full text-xs" data-testid="line-items-table">
              <thead className="bg-[#1e3a5f] text-white">
                <tr>
                  {COLS.map((c) => <th key={c.key} className="px-2 py-2 text-left font-semibold whitespace-nowrap">{c.label}</th>)}
                  <th className="px-2 py-2 w-8"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={i} className="border-t border-gray-100" data-testid={`line-item-row-${i}`}>
                    {COLS.map((c) => (
                      <td key={c.key} className="px-1 py-1">
                        <input
                          value={row[c.key]}
                          onChange={(e) => updateRow(i, c.key, e.target.value)}
                          data-testid={`row-${i}-${c.key}`}
                          className="w-full border border-gray-200 rounded px-1.5 py-1 text-xs focus:ring-1 focus:ring-[#1e3a5f] outline-none"
                        />
                      </td>
                    ))}
                    <td className="px-1 py-1 text-center">
                      <button onClick={() => removeRow(i)} data-testid={`remove-row-${i}`} className="text-slate-500 hover:text-red-500">
                        <Trash2 size={15} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Totals + receiver */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Total Weight" value={form.total_weight} onChange={(e) => set('total_weight', e.target.value)} data-testid="total-weight-input" />
          <Field label="Receiver Name (Printed) — optional" value={form.receiver_name_printed} onChange={(e) => set('receiver_name_printed', e.target.value)} data-testid="receiver-name-input" />
        </div>

        {/* Packer signature */}
        <div>
          <label className="block text-xs font-semibold text-[#1e3a5f] mb-1 uppercase tracking-wide">Packed By — Signature *</label>
          <p className="text-xs text-slate-500 mb-2">The receiver signature is left blank to be signed on paper at delivery.</p>
          <div className="border-2 border-dashed border-gray-300 rounded-lg bg-white" data-testid="packer-signature-pad">
            <SignatureCanvas ref={sigRef} canvasProps={{ className: 'w-full h-40', style: { touchAction: 'none' } }} />
          </div>
          <button
            type="button"
            onClick={() => sigRef.current && sigRef.current.clear()}
            data-testid="clear-signature-btn"
            className="mt-2 text-xs text-gray-500 hover:text-gray-800"
          >
            Clear signature
          </button>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
          <button onClick={onCancel} className="px-4 py-2 rounded-lg text-gray-600 hover:bg-gray-100 font-medium">Cancel</button>
          <button
            onClick={submit}
            disabled={submitting}
            data-testid="submit-packing-slip-btn"
            className="inline-flex items-center gap-2 bg-[#1e3a5f] hover:bg-[#16304f] disabled:opacity-60 text-white px-5 py-2 rounded-lg font-medium"
          >
            {submitting ? <Loader2 size={17} className="animate-spin" /> : <CheckCircle2 size={17} />}
            {submitting ? 'Creating…' : 'Create & File'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PackingSlips;
